/* eslint-disable no-case-declarations */
import { type LoopReducer, loop, Cmd, type Loop } from 'redux-loop';
import { type AnyAction } from 'redux';
import * as userProfileActions from '../actions/userProfileActions';
import { getUserProfile, fetchUserRoles } from '../../services/userProfile.service';
import * as userRolesActions from '../actions/userRolesActions';
import * as commonActions from '../actions/commonActions';
import { isTeamsApp } from '../../utils/commonUtils';
import { jwtDecode } from 'jwt-decode';
import { LOCAL_STORAGE } from '../../app.constant';

export interface IUserProfileState {
  userData: {
    displayName: string;
    email: string;
    isExternal: boolean;
    profileImage: string;
    sid: { preferredName: string; rawIdToken?: string };
    rawIdToken?: string;
    sharePointTokenForTeamsApp?: string;
  };
  userRoles: string[] | undefined;
  tenantId: string | undefined;
  region: string | undefined;
}

export const initialState: IUserProfileState = {
  userData: {
    displayName: '',
    email: '',
    isExternal: false,
    profileImage: '',
    sid: { preferredName: '' },
    sharePointTokenForTeamsApp: ''
  },
  userRoles: undefined,
  tenantId: undefined,
  region: undefined
};

export const userProfileReducer: LoopReducer<IUserProfileState> = (
  state: IUserProfileState = initialState,
  action: AnyAction
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 2 arguments, but got 1.
): IUserProfileState | Loop<IUserProfileState, AnyAction> => {
  switch (action.type) {
    case userProfileActions.FETCH_USER_PROFILE:
      return loop(
        {
          ...state
        },
        Cmd.list([
          Cmd.run(getUserProfile, {
            successActionCreator: isTeamsApp()
            ? userProfileActions.storeTeamsUserProfile
            : userProfileActions.storeUserProfile,
            failActionCreator: isTeamsApp()
              ? userProfileActions.storeTeamsUserProfile
              : userProfileActions.storeUserProfile,
            args: []
          })
        ])
      );
    case userRolesActions.FETCH_USER_ROLE:
      return loop(
        state,
        Cmd.list([
          Cmd.run(fetchUserRoles, {
            successActionCreator: userRolesActions.storeUserRole,
            failActionCreator: userRolesActions.fetchUserRoleFailure,
            args: []
          }),
          Cmd.action(commonActions.addLoadingIndicator(userRolesActions.FETCH_USER_ROLE))
        ])
      );

    case userProfileActions.STORE_USER_PROFILE:
      const sid = action.value?.idToken;
      const claims = action.value?.idToken?.claims || {};
      const { name, preferredUsername, acct } = claims;
      return {
        ...state,
        userData: {
          displayName: name || initialState?.userData?.displayName,
          email: preferredUsername || initialState?.userData?.email,
          isExternal: !!acct || initialState?.userData?.isExternal,
          profileImage: action.value?.image ? `data:image/png;base64, ${action.value?.image}` : '',
          sid: sid || ''
        }
      };
    
    case userProfileActions.STORE_TEAMS_USER_PROFILE:
      let userObject = action.value;
      if (!userObject?.name || !userObject?.preferred_username) {
        userObject = { ...jwtDecode(userObject.token), rawIdToken: userObject.token };
      }
      let userProfileImage = action.value?.image || localStorage.getItem(LOCAL_STORAGE.USER_PROFILE_PIC);
      let computedUserProfileImage =
        (userProfileImage != null && userProfileImage.length > 0 && !userProfileImage.includes('undefined'))
          ? `data:image/png;base64, ${userProfileImage}`
          : '';
      return {
        ...state,
        userData: {
          displayName: userObject?.name || initialState?.userData?.displayName,
          email: userObject?.preferred_username || initialState?.userData?.email,
          isExternal: false,
          profileImage: computedUserProfileImage,
          sid: {
            name: userObject?.name,
            preferredName: userObject?.preferred_username,
            rawIdToken: userObject.rawIdToken
          }
        }
      };
    
    case userProfileActions.SET_USER_SHAREPOINT_TOKEN: {
      return {
        ...state,
        userData: {
          sharePointTokenForTeamsApp: action.value
        }
      };
    }
    default:
      return state;
  }
};
